import React, { useState, useEffect } from "react";
import { useCompany } from "../../Context/CompanyContext";
import companyData from "../../Data/CompanyData"; // Importar datos de sectores
import { fetchWithAuth } from "../../Context/UserContext";
import API_URL from "../../config";
import { Card, CardContent } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Plus } from "lucide-react";
import { cn } from "../../lib/utils";
import { toast } from "sonner";

const InformacionEmpresa = () => {
  const { companies, fetchCompanies } = useCompany();
  const [editFormData, setEditFormData] = useState({
    sector: "",
    numero_empleados: "",
    certificaciones: [],
    direccion: "",
  });
  const [selectedCertifications, setSelectedCertifications] = useState([]);
  const [showCustomInput, setShowCustomInput] = useState(false); // Controla si el campo de entrada para una certificación personalizada está visible
  const [customCertification, setCustomCertification] = useState(""); // Controla el valor de la nueva certificación personalizada
  const id_company = companies.company.id_company;
  const [error] = useState(""); // Estado para el mensaje de error

  const predefinedCertifications = [
    "ISO 9001 (Calidad)",
    "ISO 14001 (Medio Ambiente)",
    "ISO 45001 (Seguridad y Salud)",
    "ISO 22000 (Seguridad Alimentaria)",
    "BRC (British Retail Consortium)",
    "HACCP (Análisis de Peligros y Puntos Críticos de Control)",
  ];

  useEffect(() => {
    if (companies) {
      setEditFormData({
        sector: companies.company?.sector || "",
        numero_empleados: companies.profile?.numero_empleados || "",
        certificaciones: companies.profile?.certificaciones || [],
        direccion: companies.profile?.direccion || "",
      });
      setSelectedCertifications(companies.profile?.certificaciones || []);
    }
  }, [companies]);

  const handleToggleCertification = (cert) => {
    setSelectedCertifications(
      (prevCertifications) =>
        prevCertifications.includes(cert)
          ? prevCertifications.filter((c) => c !== cert) // Elimina si está seleccionada
          : [...prevCertifications, cert] // Agrega si no está seleccionada
    );
  };

  const handleAddCustomCertification = () => {
    if (!customCertification.trim()) {
      toast.warning("Por favor, ingresa una certificación válida.");
      return;
    }

    // Validar el límite de 5 certificaciones personalizadas
    const customCertifications = selectedCertifications.filter(
      (cert) => !predefinedCertifications.includes(cert)
    );

    if (customCertifications.length >= 5) {
      toast.warning(
        "Solo puedes agregar un máximo de 5 certificaciones personalizadas."
      );
      return;
    }

    // Actualiza las certificaciones seleccionadas
    const newCertification = customCertification.trim();
    setSelectedCertifications((prevCertifications) => [
      ...prevCertifications,
      newCertification,
    ]);

    // Actualiza editFormData.certificaciones
    setEditFormData((prevFormData) => ({
      ...prevFormData,
      certificaciones: [...prevFormData.certificaciones, newCertification],
    }));

    setCustomCertification(""); // Limpia el campo de entrada
    setShowCustomInput(false); // Oculta el campo de entrada
  };

  const handleSectorChange = (e) => {
    const selectedSector = e.target.value;
    setEditFormData((prevFormData) => ({
      ...prevFormData,
      sector: selectedSector,
    }));
  };

  const handleRemoveCertification = (cert) => {
    setSelectedCertifications(selectedCertifications.filter((c) => c !== cert));
  };

  const handleSaveInfo = async () => {
    if (error) {
      toast.error("Por favor, soluciona el error antes de guardar.");
      return;
    }

    try {
      const response = await fetchWithAuth(
        `${API_URL}/bs/company-profile/${id_company}/update-info`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            profile: {
              sector: editFormData.sector,
              numero_empleados: editFormData.numero_empleados,
              certificaciones: selectedCertifications,
              direccion: editFormData.direccion,
            },
          }),
        }
      );

      if (response.ok) {
        toast.success("Información de la empresa actualizada exitosamente.");
        fetchCompanies(); // Actualizar la lista de empresas en el contexto global
      } else {
        const data = await response.json();
        toast.error(`Error al actualizar información de la empresa: ${data.error}`);
      }
    } catch (error) {
      console.error(
        "Error en la actualización de información de la empresa:",
        error
      );
      toast.error("Error al actualizar la información de la empresa.");
    }
  };

  return (
    <Card>
      <CardContent className="space-y-4 pt-6">
        <h3 className="text-lg font-semibold text-foreground">Información de la Empresa</h3>

        <div>
          <label htmlFor="direccion" className="mb-1.5 block text-sm font-medium text-foreground">
            Dirección:
          </label>
          <Input
            type="text"
            id="direccion"
            value={editFormData.direccion || ""}
            onChange={(e) =>
              setEditFormData((prevFormData) => ({
                ...prevFormData,
                direccion: e.target.value,
              }))
            }
            placeholder="Ingrese la dirección de la empresa"
            maxLength={500} // Límite según la BD
          />
        </div>

        <div>
          <label htmlFor="sector" className="mb-1.5 block text-sm font-medium text-foreground">
            Sector:
          </label>
          <select
            id="sector"
            name="sector"
            value={editFormData?.sector || ""}
            onChange={handleSectorChange}
            className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          >
            <option value="" disabled>
              Seleccione un sector
            </option>
            {companyData.sectores.map((sector) => (
              <option key={sector.label} value={sector.label}>
                {sector.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="numero_empleados" className="mb-1.5 block text-sm font-medium text-foreground">
            Número de Empleados:
          </label>
          <select
            id="numero_empleados"
            value={editFormData.numero_empleados || ""}
            onChange={(e) =>
              setEditFormData((prevFormData) => ({
                ...prevFormData,
                numero_empleados: e.target.value,
              }))
            }
            className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          >
            <option value="">Seleccione el número de empleados</option>
            <option value="1">1 empleado</option>
            <option value="2 - 10">2 - 10 empleados</option>
            <option value="11 - 50">11 - 50 empleados</option>
            <option value="51 - 200">51 - 200 empleados</option>
            <option value="201 - 1000">201 - 1000 empleados</option>
            <option value="1001+">Más de 1000 empleados</option>
          </select>
        </div>

        <div>
          <label className="mb-2 block text-sm font-medium text-foreground">Certificaciones:</label>
          <div className="flex flex-wrap gap-2">
            {predefinedCertifications.map((cert) => (
              <button
                key={cert}
                type="button"
                onClick={() => handleToggleCertification(cert)}
                className={cn(
                  "rounded-md border border-border px-3 py-1.5 text-sm hover:bg-muted",
                  selectedCertifications.includes(cert) && "border-primary bg-primary/10 text-primary"
                )}
              >
                {cert}
              </button>
            ))}
            {selectedCertifications
              .filter((cert) => !predefinedCertifications.includes(cert)) // Mostrar solo las personalizadas
              .map((customCert) => (
                <button
                  key={customCert}
                  type="button"
                  onClick={() => handleRemoveCertification(customCert)}
                  className="rounded-md border border-primary bg-primary/10 px-3 py-1.5 text-sm text-primary"
                >
                  {customCert} ×
                </button>
              ))}
            <button
              type="button"
              onClick={() => setShowCustomInput(!showCustomInput)}
              className="flex items-center gap-1 rounded-md border border-dashed border-border px-3 py-1.5 text-sm text-muted-foreground hover:bg-muted"
            >
              <Plus className="h-3.5 w-3.5" /> Agregar Otra
            </button>
          </div>

          {showCustomInput && (
            <div className="mt-2 flex gap-2">
              <Input
                type="text"
                value={customCertification}
                onChange={(e) => setCustomCertification(e.target.value)}
                placeholder="Agrega una certificación distinta"
                maxLength={50}
              />
              <Button type="button" variant="outline" onClick={handleAddCustomCertification}>
                OK
              </Button>
            </div>
          )}
        </div>

        <Button onClick={handleSaveInfo}>Guardar Cambios</Button>
      </CardContent>
    </Card>
  );
};

export default InformacionEmpresa;
